import { SafeCapType } from 'shared';

import { StringKey } from '../lang/index';
import { Company } from './company.types';
import { Valuation } from './events.types';
import { Safe } from './safes.types';
import { ShareClass } from './share-classes.types';
import { Stakeholder, StakeholderType } from './stakeholder.types';
import { VestingTask } from './vesting-tasks.types';

export enum ScenarioType {
  FUNDRAISING = 'fundraising-round',
  LIQUIDITY = 'liquidity-event',
}

export enum DilutionImpact {
  POST_ROUND = 'post-round',
  PRE_ROUND = 'pre-round',
}

export enum GrantItemFilter {
  GRANTS = 'grants',
  SARS = 'sars',
}

export const ScenarioTypeTitle: Record<ScenarioType, StringKey> = {
  [ScenarioType.FUNDRAISING]: 'UPPERCASE_FUNDRAISING_ROUND',
  [ScenarioType.LIQUIDITY]: 'LIQUIDITY_EVENT',
};

export const DilutionImpactTitle: Record<DilutionImpact, string> = {
  [DilutionImpact.PRE_ROUND]: 'Pre-Round',
  [DilutionImpact.POST_ROUND]: 'Post-Round',
};

export type CreateScenarioDto = {
  currentCapTable?: boolean;
  name: string;
  type: ScenarioType;
  valuationId?: string;
};

export type PatchScenarioDto = {
  accelerateVesting?: boolean;
  closingDate?: null | string;
  currentCapTable?: boolean;
  isHasBeenChanged?: boolean;
  name?: string;
  preMoneyValuation?: number;
  type?: ScenarioType;
  valuationId?: string;
};

export type ScenarioBase = {
  accelerateVesting: boolean;
  closingDate: Date;
  currentValuation: boolean;
  isGeneratedFirstCapTable: boolean;
  isHasBeenChanged: boolean;
  name: string;
  preMoneyValuation: number;
  type: ScenarioType;
  valuation?: Valuation;
};

export type ScenarioGrant = {
  createdAt: Date;
  id: string;
  issuedShares: number;
  payout: number;
  scenario: Scenario;
  updatedAt: Date;
  vestingTask: VestingTask;
};

export type PoolBase = {
  dilutedShares: number;
  name: string;
  shareClass: ShareClass;
  type: DilutionImpact;
};

export interface ScenarioCapTableByStakeholderTypeShareClass {
  createdAt: Date;
  id: string;
  isDefault: boolean;
  issued: number;
  issuedChange: number;
  name: string;
  shareClass: ShareClass;
  updatedAt: Date;
}

export interface CapTableByStakeholderTypeBase {
  avgSharePrice: number;

  avgSharePriceChange: number;
  convertedBasis: number;
  convertedBasisChange: number;
  convertedBasisPercentage: number;
  convertedBasisPercentageChange: number;
  diluted: number;

  dilutedChange: number;
  dilutedPercentage: number;
  dilutedPercentageChange: number;
  investment: number;

  investmentChange: number;
  issued: number;
  issuedChange: number;
  issuedPercentage: number;
  issuedPercentageChange: number;
  shareClasses: ScenarioCapTableByStakeholderTypeShareClass[];

  type?: StakeholderType;
  voting: number;
  votingChange: number;
  votingPercentage: number;

  votingPercentageChange: number;
}

export interface ScenarioCapTableByStakeholderType extends CapTableByStakeholderTypeBase {
  createdAt: Date;
  id: string;
  updatedAt: Date;
}

export interface ScenarioCapTableByStakeholderShareClass {
  capTableRecord: ScenarioCapTableByStakeholder;
  createdAt: Date;
  id: string;
  isDefault: boolean;
  issued: number;
  issuedChange: number;
  name: string;
  shareClass: ShareClass;
  updatedAt: Date;
}

export interface CapTableByStakeholderBase extends ScenarioStakeholder {
  avgSharePrice: number;

  avgSharePriceChange: number;
  convertedBasis: number;
  convertedBasisChange: number;
  convertedBasisPercentage: number;
  convertedBasisPercentageChange: number;
  diluted: number;

  dilutedChange: number;
  dilutedPercentage: number;
  dilutedPercentageChange: number;
  investment: number;

  investmentChange: number;
  issued: number;
  issuedChange: number;
  issuedPercentage: number;
  issuedPercentageChange: number;
  name: string;

  shareClasses: ScenarioCapTableByStakeholderShareClass[];
  voting: number;
  votingChange: number;
  votingPercentage: number;

  votingPercentageChange: number;
}

export interface ScenarioCapTableByStakeholder extends CapTableByStakeholderBase {
  createdAt: Date;
  id: string;
  updatedAt: Date;
}

export interface ScenarioCapTableByShareClassStakeholder extends ScenarioStakeholder {
  avgSharePrice: number;
  avgSharePriceChange: number;

  capTableRecord: ScenarioCapTableByShareClass;
  convertedBasis: number;
  convertedBasisChange: number;
  convertedBasisPercentage: number;
  convertedBasisPercentageChange: number;
  createdAt: Date;
  diluted: number;

  dilutedChange: number;
  dilutedPercentage: number;
  dilutedPercentageChange: number;
  id: string;

  investment: number;
  investmentChange: number;
  issued: number;
  issuedChange: number;
  issuedPercentage: number;
  issuedPercentageChange: number;

  name: string;
  updatedAt: Date;
  voting: number;
  votingChange: number;
  votingPercentage: number;
  votingPercentageChange: number;
}

export interface CapTableByShareClassBase {
  avgSharePrice: number;
  avgSharePriceChange: number;
  convertedBasis: number;
  convertedBasisChange: number;
  convertedBasisPercentage: number;
  convertedBasisPercentageChange: number;
  diluted: number;
  dilutedChange: number;

  dilutedPercentage: number;
  dilutedPercentageChange: number;
  investment: number;
  investmentChange: number;

  isDefault: boolean;
  issued: number;
  issuedChange: number;
  issuedPercentage: number;
  issuedPercentageChange: number;
  name: string;

  shareClass: ShareClass;
  stakeholders: ScenarioCapTableByShareClassStakeholder[];
  voting: number;
  votingChange: number;

  votingPercentage: number;
  votingPercentageChange: number;
}

export interface ScenarioCapTableByShareClass extends CapTableByShareClassBase {
  createdAt: Date;
  id: string;
  updatedAt: Date;
}

export interface LiquidityCapTableByShareClassStakeholder extends ScenarioStakeholder {
  avgSharePrice: number;
  avgSharePriceChange: number;
  capParticipating: number;
  capParticipatingChange: number;
  capTableRecord: LiquidityCapTableByShareClass;
  commonStockProceeds: number;
  convertedBasis: number;
  convertedBasisChange: number;

  convertedBasisPercentage: number;
  convertedBasisPercentageChange: number;
  createdAt: Date;
  diluted: number;
  dilutedChange: number;

  dilutedPercentage: number;
  dilutedPercentageChange: number;
  effectiveLiquidityProceeds: number;
  id: string;
  investment: number;
  investmentChange: number;
  investmentDays: number;
  issued: number;
  issuedChange: number;
  issuedPercentage: number;

  issuedPercentageChange: number;
  minLiquidityProceeds: number;

  multiple: number;
  multipleChange: number;

  name: string;
  participatingOnProceeds: number;
  proceedsWithoutLiquidity: number;
  roi: number;
  seniorityColumns: LiquidityCapTableByStakeholderSeniority[];
  updatedAt: Date;
  voting: number;
  votingChange: number;

  votingPercentage: number;

  votingPercentageChange: number;
}

export type LiquidityCapTableByStakeholderSeniority = {
  capTableRecord: LiquidityCapTableByStakeholder;
  createdAt: Date;
  id: string;
  seniorityIndex: number;
  seniorityValue: number;
  updatedAt: Date;
};

type LiquidityCapTableByStakeholderTypeBase = {
  avgSharePrice: number;

  avgSharePriceChange: number;
  commonStockProceeds: number;
  convertedBasis: number;
  convertedBasisChange: number;
  convertedBasisPercentage: number;

  convertedBasisPercentageChange: number;
  diluted: number;
  dilutedChange: number;
  dilutedPercentage: number;
  dilutedPercentageChange: number;

  effectiveLiquidityProceeds: number;
  investment: number;
  investmentChange: number;
  issued: number;
  issuedChange: number;
  issuedPercentage: number;
  issuedPercentageChange: number;
  minLiquidityProceeds: number;
  participatingOnProceeds: number;
  //new
  proceedsWithoutLiquidity: number;

  roi: number;
  seniorityColumns: LiquidityCapTableByStakeholderSeniority[];
  shareClasses: LiquidityCapTableByStakeholderTypeShareClass[];
  type?: StakeholderType;
  voting: number;
  votingChange: number;
  votingPercentage: number;
  votingPercentageChange: number;
};

export interface LiquidityCapTableByStakeholderType extends LiquidityCapTableByStakeholderTypeBase {
  createdAt: Date;
  id: string;
  scenario: Scenario;
  updatedAt: Date;
}

export type LiquidityCapTableByStakeholderTypeShareClass = {
  capParticipating: number;
  capParticipatingChange: number;
  capTableRecord: LiquidityCapTableByStakeholderType;
  createdAt: Date;
  id: string;
  investment: number;
  investmentChange: number;
  investmentDays: number;
  isDefault: boolean;

  issued: number;
  issuedChange: number;
  multiple: number;
  multipleChange: number;
  name: string;
  seniorityValue: number;
  shareClass: ShareClass;
  updatedAt: Date;
};

interface LiquidityCapTableByStakeholderBase extends ScenarioStakeholder {
  avgSharePrice: number;

  avgSharePriceChange: number;
  commonStockProceeds: number;
  convertedBasis: number;
  convertedBasisChange: number;
  convertedBasisPercentage: number;

  convertedBasisPercentageChange: number;
  diluted: number;
  dilutedChange: number;
  dilutedPercentage: number;
  dilutedPercentageChange: number;

  effectiveLiquidityProceeds: number;
  investment: number;
  investmentChange: number;
  issued: number;
  issuedChange: number;
  issuedPercentage: number;
  issuedPercentageChange: number;
  minLiquidityProceeds: number;
  name: string;
  participatingOnProceeds: number;

  //new
  proceedsWithoutLiquidity: number;
  roi: number;
  seniorityColumns: LiquidityCapTableByStakeholderSeniority[];
  shareClasses: LiquidityCapTableByStakeholderShareClass[];
  voting: number;
  votingChange: number;
  votingPercentage: number;
  votingPercentageChange: number;
}

export interface LiquidityCapTableByStakeholder extends LiquidityCapTableByStakeholderBase {
  createdAt: Date;
  id: string;
  scenario: Scenario;
  updatedAt: Date;
}

export type LiquidityCapTableByStakeholderShareClass = {
  capParticipating: number;
  capParticipatingChange: number;
  capTableRecord: LiquidityCapTableByStakeholder;
  createdAt: Date;
  id: string;
  investment: number;
  investmentChange: number;
  investmentDays: number;
  isDefault: boolean;
  issued: number;
  issuedChange: number;
  multiple: number;
  multipleChange: number;
  name: string;
  seniorityColumns: LiquidityCapTableByStakeholderSeniority[];
  shareClass: ShareClass;
  updatedAt: Date;
};

type LiquidityCapTableByShareClassBase = {
  avgSharePrice: number;
  avgSharePriceChange: number;
  capParticipating: number;

  capParticipatingChange: number;
  commonStockProceeds: number;
  convertedBasis: number;
  convertedBasisChange: number;
  convertedBasisPercentage: number;
  convertedBasisPercentageChange: number;
  diluted: number;
  dilutedChange: number;
  dilutedPercentage: number;
  dilutedPercentageChange: number;

  effectiveLiquidityProceeds: number;
  investment: number;
  investmentChange: number;
  investmentDays: number;
  isDefault: boolean;
  issued: number;
  issuedChange: number;
  issuedPercentage: number;
  issuedPercentageChange: number;
  minLiquidityProceeds: number;

  multiple: number;
  multipleChange: number;

  name: string;
  participatingOnProceeds: number;

  proceedsWithoutLiquidity: number;
  roi: number;
  seniorityColumns: LiquidityCapTableByStakeholderSeniority[];
  shareClass: ShareClass;
  stakeholders: LiquidityCapTableByShareClassStakeholder[];
  voting: number;
  votingChange: number;

  votingPercentage: number;

  votingPercentageChange: number;
};

export interface LiquidityCapTableByShareClass extends LiquidityCapTableByShareClassBase {
  createdAt: Date;
  id: string;
  scenario: Scenario;
  updatedAt: Date;
}

export interface ScenarioPool extends PoolBase {
  createdAt: Date;
  id: string;
  scenario: Scenario;
  updatedAt: Date;
}

export interface InvestmentBase extends ScenarioStakeholder {
  investment: number;
  shareClass: ShareClass;
}

export interface ScenarioInvestment extends InvestmentBase {
  createdAt: Date;
  id: string;
  scenario: Scenario;
  updatedAt: Date;
}

export interface ScenarioCustomSafeBase extends ScenarioStakeholder {
  cap: number;
  capType: null | SafeCapType;
  discount: number;
  floor: number;
  investment: number;
  shareClass: ShareClass;
}

export interface ScenarioCustomSafe extends ScenarioCustomSafeBase {
  createdAt: Date;
  id: string;
  scenario: Scenario;
  updatedAt: Date;
}

export type ScenarioSafe = {
  createdAt: Date;
  id: string;
  safe: Safe;
  scenario: Scenario;
  shareClass: ShareClass;
  updatedAt: Date;
};

export type ScenarioStakeholder = {
  fakeStakeholderName?: string;
  stakeholder?: Stakeholder;
};

export interface Scenario extends ScenarioBase {
  company: Company;
  createdAt: Date;
  creator: Stakeholder;
  customSafes: ScenarioCustomSafe[];
  grants: ScenarioGrant[];
  id: string;
  investments: ScenarioInvestment[];
  pools: ScenarioPool[];
  safes: ScenarioSafe[];
  updatedAt: Date;
}

export type CreateScenarioPool = {
  dilutedShares?: number;
  name?: string;
  shareClassId?: string;
  type?: DilutionImpact;
};

export type CreateCustomSafe = {
  cap?: number;
  capType?: null | SafeCapType;
  discount?: number;
  fakeStakeholderName?: string;
  floor?: number;
  investment?: number;
  shareClassId?: string;
  stakeholderId?: string;
};

export type CreateScenarioSafe = {
  safeId?: string;
  shareClassId?: string;
};

export type CreateScenarioInvestment = {
  fakeStakeholderName?: string;
  investment?: number;
  shareClassId?: string;
  stakeholderId?: string;
};

export type CreateScenarioGrant = {
  issuedShares?: number;
  payout?: number;
  vestingTaskId?: string;
};

export type CreateScenarioClassConversion = {
  conversionRatio: number;
  preferredShares: number;
  shareClassId: string;
  stakeholderId: string;
};

export type ClassConversionTask = {
  conversionRatio: number;
  createdAt: Date;
  id: string;
  preferredShares: number;
  scenario: Scenario;
  shareClass: ShareClass;
  stakeholder: Stakeholder;
  updatedAt: Date;
};

export type ScenarioClassConversion = {
  classConversionTask: ClassConversionTask;
  conversionRatio: number;
  createdAt: Date;
  id: string;
  preferredShares: number;
  scenario: Scenario;
  shareClass: ShareClass;
  stakeholder: Stakeholder;
  updatedAt: Date;
};

export type ConversionSummary = {
  investments: number;
  isHurdleTriggered: boolean;
  issuedShares: number;
  postMoneyValuation: number;
  safeInvestments: number;
  sharePrice: number;
  sharesIssuedDuringScenario: number;
  stakeholderLiquidityProceeds?: number;
  stakeholderOwnershipPercentage?: number;
};
